import { AuthService } from './../../core/authentication/auth.service';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, OnInit, effect, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { WorkerProfile } from 'src/app/models/WorkerProfile';
import { WorkerIllnessListDataSource } from './worker-illness-list.datasource';
import { SickLeaveDto } from 'src/app/models/dtos/sick-leave-dto';
import { SickLeaveModalComponent } from '../sick-leaves-modal/sick-leaves-modal.component';
import { AbsencesRefreshService } from 'src/app/absences/services/absences-refresh.service';

@Component({
  selector: 'app-worker-illness-list',
  templateUrl: './worker-illness-list.component.html',
  styleUrls: ['./worker-illness-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkerIllnessListComponent implements OnInit, AfterViewInit {
  dataSource = inject(WorkerIllnessListDataSource);
  authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private refreshService = inject(AbsencesRefreshService);
  private readonly destroyRef = inject(DestroyRef);

  readonly profile = signal<WorkerProfile | undefined>(undefined);
  readonly displayedColumns = ['employerObject', 'reason', 'startDate', 'calendarTimeTicks', 'status', 'actions'];
  readonly displayedColumnsMobile = ['reason', 'calendarTimeTicks', 'status', 'actions'];

  private readonly paginator = viewChild(MatPaginator);
  private readonly sort = viewChild(MatSort);
  readonly workerAgreementId = input<number | null>(null);

  constructor() {
    effect(() => {
      const profile = this.profile();
      const paginator = this.paginator();
      const sort = this.sort();
      // Re-run the load whenever the selected agreement or a refresh is requested.
      this.workerAgreementId();
      this.refreshService.refreshToken();

      if (!profile?.WorkerId || !paginator || !sort) {
        return;
      }

      this.fetchSickLeaves();
    });
  }

  ngOnInit(): void {
    this.route.parent!.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => this.profile.set(data['workerProfile']));
  }

  ngAfterViewInit(): void {
    const sort = this.sort()!;
    const paginator = this.paginator()!;

    merge(sort.sortChange, this.translateService.onLangChange)
      .pipe(tap(() => (paginator.pageIndex = 0)), takeUntilDestroyed(this.destroyRef))
      .subscribe();

    merge(sort.sortChange, paginator.page, this.translateService.onLangChange)
      .pipe(tap(() => this.fetchSickLeaves()), takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  openDetailsModal(record: SickLeaveDto): void {
    if (!record) {
      return;
    }

    this.dialog
      .open(SickLeaveModalComponent, {
        maxHeight: '90vh',
        maxWidth: '220vh',
        data: { SickLeaveId: record.Id },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.refreshService.refresh();
        }
      });
  }

  private fetchSickLeaves(): void {
    const profile = this.profile();
    const paginator = this.paginator();
    const sort = this.sort();
    if (!profile?.WorkerId || !paginator || !sort) {
      return;
    }

    this.dataSource.sickLeavesSubject.next({
      WorkerId: profile.WorkerId,
      WorkerAgreementId: this.workerAgreementId(),
      Page: paginator.pageIndex + 1,
      PageSize: paginator.pageSize,
      SortingField: sort.active,
      SortingDirection: sort.direction,
    });
  }
}
